import { getStatus } from '@ms/yammer-data-support';
import type { EntityStatus } from '@ms/yammer-data-support';
import createCachedSelector from 're-reselect';

import type { Group, ViewerCommunityIdSets, ViewerCommunityRelationship, YammerState } from '../../state/types';
import {
  getFulfilledUnseenThreadEdgesForFeed,
  hasUnseenGroupFeedThreadStarters,
  hasUnseenGroupFeedThreads,
  reorderGroupIdsWithUnseenFirst,
} from '../feedCounts/selectors';
import { isJoined } from '../viewerCommunityRelationships/selectors';

interface GroupIds {
  readonly favoriteIds: Group['id'][];
  readonly nonFavoriteIds: Group['id'][];
  readonly adminIds: Group['id'][];
}

const emptyCommunityIds: Group['id'][] = [];
type GetViewerCommunityIds = (state: YammerState) => Group['id'][];
export const getViewerCommunityIds: GetViewerCommunityIds = (state: YammerState) =>
  state.viewerCommunityIdSets.fulfilled?.communityIds ?? emptyCommunityIds;

type GetFavoriteCommunityPosition = (state: YammerState, groupId: Group['id']) => number;
export const getFavoriteCommunityPosition: GetFavoriteCommunityPosition = (state, groupId) =>
  state.viewerCommunityIdSets.fulfilled?.favoriteCommunityIds.indexOf(groupId) ?? -1;

type GetRankedCommunityIdsThrows = (state: YammerState) => GroupIds;
export const getRankedCommunityIdsThrows: GetRankedCommunityIdsThrows = createCachedSelector(
  (state: YammerState) => state.viewerCommunityRelationships?.fulfilled?.byId,
  (state: YammerState) => state.viewerCommunityIdSets,
  (
    viewerCommunityRelationships: Record<string, ViewerCommunityRelationship>,
    viewerCommunityIdSets: ViewerCommunityIdSets
  ) => {
    if (!viewerCommunityIdSets.fulfilled) {
      throw new Error('viewerCommunityIdSets is not fulfilled yet!');
    }
    const { communityIds, favoriteCommunityIds } = viewerCommunityIdSets.fulfilled;

    return {
      favoriteIds: favoriteCommunityIds,
      nonFavoriteIds: communityIds.filter((id) => !favoriteCommunityIds.includes(id)),
      adminIds: communityIds.filter((id) => !!viewerCommunityRelationships?.[id]?.viewerIsAdmin),
    };
  }
)((_state) => 'cache');

type GetGroupNavigationLoadStatus = (state: YammerState) => EntityStatus;
export const getGroupNavigationLoadStatus: GetGroupNavigationLoadStatus = (state: YammerState) =>
  getStatus(state.viewerCommunityIdSets);

type GetNextUnseenGroup = (
  state: YammerState,
  groupId: Group['id'],
  useThreadStarters?: boolean
) => Group['id'] | undefined;
export const getNextUnseenGroup: GetNextUnseenGroup = (state, groupId, useThreadStarters) => {
  if (!state.viewerCommunityIdSets.fulfilled) {
    return undefined;
  }
  const { communityIds: groupIds } = state.viewerCommunityIdSets.fulfilled;

  const hasUnseen = useThreadStarters ? hasUnseenGroupFeedThreadStarters : hasUnseenGroupFeedThreads;
  const isGroupUnseen = (groupId: Group['id']) => hasUnseen(state, groupId) && isJoined(state, groupId);

  if (!groupIds.includes(groupId)) {
    return groupIds.find(isGroupUnseen);
  }

  const index = groupIds.indexOf(groupId);
  const groupsAfterCurrent = groupIds.slice(index + 1);
  const groupsBeforeCurrent = groupIds.slice(0, index);

  const unseenGroupAfterCurrent = groupsAfterCurrent.find(isGroupUnseen);
  if (unseenGroupAfterCurrent) {
    return unseenGroupAfterCurrent;
  }

  return groupsBeforeCurrent.find(isGroupUnseen);
};

type GetViewerCommunitiesWithUnreadGroupsFirst = (state: YammerState) => Group['id'][];
export const getViewerCommunitiesWithUnreadGroupsFirst: GetViewerCommunitiesWithUnreadGroupsFirst =
  createCachedSelector(
    (state: YammerState) => state.viewerCommunityIdSets?.fulfilled,
    getFulfilledUnseenThreadEdgesForFeed,
    (viewerCommunityIdSetsFulfilled, unseenThreadEdges) =>
      reorderGroupIdsWithUnseenFirst(unseenThreadEdges, viewerCommunityIdSetsFulfilled?.communityIds || [])
  )((_state) => 'cache');

type IsViewerCommunitiesCountGreaterThanEqualTo = (state: YammerState, communitiesCount: number) => boolean;
export const isViewerCommunitiesCountGreaterThanEqualTo: IsViewerCommunitiesCountGreaterThanEqualTo = (
  state,
  communitiesCount
) => {
  const communityIds = getViewerCommunityIds(state);

  return communityIds.length >= communitiesCount;
};
